import { useEffect, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Star, Plus, Edit2, Trash2, EyeOff, Eye } from "lucide-react";

const EMPTY = { guest_name: "", rating: 5, title: "", comment: "", avatar_url: "", source: "admin" };

export default function ReviewsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => { const { data } = await api.get("/admin/reviews"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editing) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success(editing ? "Review updated" : "Review added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleHide = async (id) => { try { await api.post(`/admin/reviews/${id}/toggle-hide`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const remove = async (id) => { if (!confirm("Delete this review?")) return; try { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Reviews</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Add guest testimonials, moderate toxic comments and control what appears on the public site.</p>
        </div>
        <Button onClick={openNew} data-testid="new-review-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><Plus className="w-4 h-4 mr-1" /> Add review</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => (
          <Card key={r.id} className={r.status === "hidden" ? "opacity-60" : ""} data-testid={`review-card-${r.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading text-base text-[color:var(--deep)]">{r.guest_name}</p>
                  <div className="flex mt-1">{[...Array(5)].map((_, i) => (<Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />))}</div>
                </div>
                <Badge className={r.status === "hidden" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}>{r.status}</Badge>
              </div>
              {r.title && <p className="mt-3 font-medium text-sm text-[color:var(--deep)]">&ldquo;{r.title}&rdquo;</p>}
              <p className="mt-1 text-sm text-[color:var(--muted-text)] line-clamp-4">{r.comment}</p>
              <p className="text-[10px] uppercase tracking-wider text-[color:var(--muted-text)] mt-3">{r.source} · {formatDateTime(r.created_at)}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-review-${r.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => toggleHide(r.id)} data-testid={`hide-review-${r.id}`}>{r.status === "hidden" ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</Button>
                <Button size="sm" variant="destructive" onClick={() => remove(r.id)} data-testid={`del-review-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && <p className="col-span-full text-center py-10 text-sm text-[color:var(--muted-text)]">No reviews yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit review" : "Add review"}</DialogTitle><DialogDescription>Reviews appear on the landing page &quot;What guests say&quot; section.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Guest name</Label><Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="review-name" /></div>
            <div><Label>Rating</Label>
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
                <SelectTrigger data-testid="review-rating"><SelectValue /></SelectTrigger>
                <SelectContent>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{"★".repeat(n)}{"☆".repeat(5 - n)} ({n})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Headline (optional)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Perfect safari base" data-testid="review-title" /></div>
            <div><Label>Comment</Label><Textarea rows={4} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="review-comment" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-review-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">{editing ? "Save" : "Add review"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
